import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";

export default function ContactPage() {
  const [params] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "General inquiry",
    message: "", vehicle_id: params.get("vehicle") || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post("/contact", form); setSubmitted(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Get in touch</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3"><span className="chrome-text">Talk to a</span> <span className="gold-text">human.</span></h1>
          <p className="text-slate-300">Questions about a vehicle, financing, or service? We respond within the hour.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {submitted ? (
            <div className="surface p-12 rounded-md text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "var(--wakzy-gold)" }} />
              <h2 className="font-heading text-3xl mb-2 chrome-text inline-block">Thanks, {form.name.split(" ")[0]}.</h2>
              <p className="text-slate-300">A sales advisor will reach out within the hour.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="surface p-8 rounded-md space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-sm">Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" data-testid="contact-name" /></div>
                <div><Label className="text-sm">Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" data-testid="contact-email" /></div>
                <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
                <div><Label className="text-sm">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
                <div className="md:col-span-2"><Label className="text-sm">Message *</Label><Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="bg-[#04060d] border-white/10 mt-1" data-testid="contact-message" /></div>
              </div>
              <Button type="submit" disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="contact-submit-btn">{submitting ? "Sending…" : "Send Message"}</Button>
            </form>
          )}
        </div>
        <div className="space-y-4">
          {[
            { icon: MapPin, label: "Visit", lines: ["Wakzy Auto Sales & Services LLC", "121 Rainbow Way", "Fayetteville, GA 30214"] },
            { icon: Phone, label: "Call", lines: ["(770) 647-2563"] },
            { icon: Mail, label: "Email", lines: ["info@wakzyauto.com"] },
            { icon: Clock, label: "Hours", lines: ["Mon–Sat · 9am – 8pm", "Sunday · 11am – 5pm"] },
          ].map((c, i) => (
            <div key={i} className="surface p-5 rounded-md">
              <c.icon className="w-5 h-5 mb-3" style={{ color: "var(--wakzy-gold)" }} />
              <div className="label-eyebrow mb-1">{c.label}</div>
              {c.lines.map((l, j) => <div key={j} className="text-sm text-slate-300">{l}</div>)}
            </div>
          ))}
        </div>
      </section>
    </PublicShell>
  );
}
